import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import DnsRoundedIcon from "@material-ui/icons/DnsRounded";
import RefreshRoundedIcon from "@material-ui/icons/RefreshRounded";
import OpenInNewRoundedIcon from "@material-ui/icons/OpenInNewRounded";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

/**
 * CustomDomainsManager — super-admin read-only view of every tenant custom
 * domain and the company it points to. Sources GET /api/admin/custom-domains
 * (isAuth + isSuper). No mutation here — operators activate/cancel domains
 * through the tenant Branding wizard; this is the at-a-glance directory.
 */

const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24 };
const TT = { min: 44 };

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surface = isDark ? "#13181a" : "#ffffff";

  return {
    root: { color: fg },
    header: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      marginBottom: SP.md,
    },
    titleIcon: {
      display: "grid",
      placeItems: "center",
      width: 40,
      height: 40,
      borderRadius: 12,
      background: accentSoft,
      color: accent,
    },
    title: { fontSize: 20, fontWeight: 700, lineHeight: 1.2 },
    subtitle: { fontSize: 13, color: fgMuted, marginTop: 2 },
    refreshBtn: {
      marginInlineStart: "auto",
      minWidth: TT.min,
      minHeight: TT.min,
      color: accent,
    },
    // Horizontal scroll on narrow phones so the table never clips.
    tableWrap: {
      border: `1px solid ${hair}`,
      borderRadius: 14,
      background: surface,
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: 640,
    },
    th: {
      textAlign: "start",
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      color: fgMuted,
      padding: `${SP.sm}px ${SP.md}px`,
      borderBottom: `1px solid ${hair}`,
      whiteSpace: "nowrap",
    },
    td: {
      fontSize: 14,
      color: fg,
      padding: `${SP.sm}px ${SP.md}px`,
      borderBottom: `1px solid ${hair}`,
      verticalAlign: "middle",
    },
    domainCell: { display: "flex", alignItems: "center", gap: SP.xs },
    domainLink: {
      color: accent,
      textDecoration: "none",
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
    },
    extIcon: { fontSize: 15, marginInlineStart: 4, opacity: 0.7 },
    companyName: { fontWeight: 500 },
    companyId: { color: fgMuted, fontSize: 12, marginInlineStart: 6 },
    chip: {
      display: "inline-block",
      fontSize: 12,
      fontWeight: 600,
      padding: "3px 10px",
      borderRadius: 999,
      whiteSpace: "nowrap",
    },
    muted: { color: fgMuted },
    center: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: SP.sm,
      padding: `${SP.xl}px ${SP.md}px`,
      color: fgMuted,
    },
  };
});

// Status → chip colours. Active green, in-flight amber, failed red, off grey.
const statusStyle = (status, isDark) => {
  const s = String(status || "").toLowerCase();
  const green = { color: isDark ? "#2ee37a" : "#0f7a4f", bg: "rgba(37,211,102,0.14)" };
  const amber = { color: isDark ? "#fcd34d" : "#92660a", bg: "rgba(245,158,11,0.16)" };
  const red = { color: isDark ? "#fca5a5" : "#b91c1c", bg: "rgba(239,68,68,0.14)" };
  const grey = { color: isDark ? "rgba(255,255,255,0.6)" : "#52525b", bg: "rgba(120,120,120,0.14)" };
  if (s === "active") return green;
  if (s.startsWith("pending")) return amber;
  if (s.startsWith("failed")) return red;
  return grey;
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch (_e) {
    return "—";
  }
};

const CustomDomainsManager = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/api/admin/custom-domains");
      setRows(Array.isArray(data?.domains) ? data.domains : []);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          i18n.t("adminDomains.loadError", {
            defaultValue: "Could not load custom domains",
          })
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cols = useMemo(
    () => [
      i18n.t("adminDomains.col.domain", { defaultValue: "Domain" }),
      i18n.t("adminDomains.col.company", { defaultValue: "Company" }),
      i18n.t("adminDomains.col.status", { defaultValue: "Status" }),
      i18n.t("adminDomains.col.kind", { defaultValue: "Type" }),
      i18n.t("adminDomains.col.cert", { defaultValue: "Certificate" }),
      i18n.t("adminDomains.col.activated", { defaultValue: "Activated" }),
    ],
    []
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleIcon}>
          <DnsRoundedIcon />
        </div>
        <div>
          <Typography className={classes.title}>
            {i18n.t("adminDomains.title", { defaultValue: "Custom Domains" })}
          </Typography>
          <Typography className={classes.subtitle}>
            {i18n.t("adminDomains.subtitle", {
              defaultValue: "Every tenant custom domain and the company it points to",
            })}
          </Typography>
        </div>
        <Tooltip title={i18n.t("adminDomains.refresh", { defaultValue: "Refresh" })}>
          <span className={classes.refreshBtn}>
            <IconButton onClick={load} disabled={loading} aria-label="refresh" style={{ color: "inherit" }}>
              <RefreshRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {loading ? (
        <Box className={classes.center}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box className={classes.center}>
          <Typography>{error}</Typography>
        </Box>
      ) : rows.length === 0 ? (
        <Box className={classes.center}>
          <DnsRoundedIcon style={{ fontSize: 38, opacity: 0.4 }} />
          <Typography>
            {i18n.t("adminDomains.empty", {
              defaultValue: "No custom domains configured yet",
            })}
          </Typography>
        </Box>
      ) : (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c} className={classes.th}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = statusStyle(r.status, isDark);
                return (
                  <tr key={`${r.companyId}-${r.domain}`}>
                    <td className={classes.td}>
                      <span className={classes.domainCell}>
                        <a
                          className={classes.domainLink}
                          href={`https://${r.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {r.domain}
                          <OpenInNewRoundedIcon className={classes.extIcon} />
                        </a>
                      </span>
                    </td>
                    <td className={classes.td}>
                      <span className={classes.companyName}>{r.companyName}</span>
                      <span className={classes.companyId}>#{r.companyId}</span>
                    </td>
                    <td className={classes.td}>
                      <span
                        className={classes.chip}
                        style={{ color: st.color, background: st.bg }}
                      >
                        {r.status || "—"}
                      </span>
                    </td>
                    <td className={classes.td}>{r.kind || "—"}</td>
                    <td className={classes.td}>{r.certStatus || "—"}</td>
                    <td className={`${classes.td} ${classes.muted}`}>
                      {fmtDate(r.activatedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomDomainsManager;
